import React from 'react';

interface MetricCardData {
  title: string;
  value: string | number;
  change: { value: number; type: 'increase' | 'decrease' };
  icon: React.ReactNode;
  gradient: string;
  iconBg: string;
}

interface DashboardMetricCardsProps {
  metrics: {
    totalUsers: number;
    activeBookings: number;
    fleetUtilization: number;
    revenue: number;
    revenueChange: number;
    bookingsChange: number;
    utilizationChange: number;
    usersChange: number;
  } | null;
  loading?: boolean;
}

const DashboardMetricCards: React.FC<DashboardMetricCardsProps> = ({ metrics, loading = false }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-xl shadow p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-8 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  const cards: MetricCardData[] = [
    {
      title: 'Total Customers',
      value: metrics?.totalUsers?.toLocaleString() || '0',
      change: { value: Math.abs(metrics?.usersChange || 0), type: metrics?.usersChange && metrics.usersChange >= 0 ? 'increase' : 'decrease' },
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      ),
      gradient: 'from-blue-500 to-blue-600',
      iconBg: 'bg-blue-400/20'
    },
    {
      title: 'Active Bookings',
      value: metrics?.activeBookings?.toString() || '0',
      change: { value: Math.abs(metrics?.bookingsChange || 0), type: metrics?.bookingsChange && metrics.bookingsChange >= 0 ? 'increase' : 'decrease' },
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      gradient: 'from-green-500 to-green-600',
      iconBg: 'bg-green-400/20'
    },
    {
      title: 'Fleet Utilization',
      value: `${metrics?.fleetUtilization || 0}%`,
      change: { value: Math.abs(metrics?.utilizationChange || 0), type: metrics?.utilizationChange && metrics.utilizationChange >= 0 ? 'increase' : 'decrease' },
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      gradient: 'from-yellow-500 to-yellow-600',
      iconBg: 'bg-yellow-400/20'
    },
    {
      title: 'Revenue',
      value: `$${metrics?.revenue?.toLocaleString() || '0'}`,
      change: { value: Math.abs(metrics?.revenueChange || 0), type: metrics?.revenueChange && metrics.revenueChange >= 0 ? 'increase' : 'decrease' },
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      gradient: 'from-purple-500 to-purple-600',
      iconBg: 'bg-purple-400/20'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {cards.map((card, index) => (
        <div
          key={index}
          className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${card.gradient} p-6 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 cursor-pointer group`}
        >
          {/* Background decoration */}
          <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10 group-hover:scale-150 transition-transform duration-500"></div>
          
          <div className="relative flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-white opacity-90 mb-2">
                {card.title}
              </p>
              <p className="text-3xl font-bold text-white tracking-tight mb-2">
                {card.value}
              </p>
              <div className="flex items-center gap-1">
                {card.change.type === 'increase' ? (
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
                <span className="text-sm font-medium text-white">
                  {card.change.value}% vs last week
                </span>
              </div>
            </div>
            <div className={`${card.iconBg} text-white p-3 rounded-lg group-hover:scale-110 transition-transform duration-300`}>
              {card.icon}
            </div>
          </div>

          {/* Hover indicator */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/30 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
        </div>
      ))}
    </div>
  );
};

export default DashboardMetricCards;
